import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Alert,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  useGridApiRef,
} from "@mui/x-data-grid";
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  AggregatedMatchesSummary,
  useCityEscapesSummaries,
} from "../../../hooks/cityEscapeSummaries.hooks";
import { useFirstColumnStickyStyles } from "../../../hooks/datagrid.hooks";
import { useProductSummaries } from "../../../hooks/products.hooks";
import { MatchFilter } from "../../../libs/statsFilters";
import { formatSecondsToDDHHMMSS } from "../../../libs/utils";
import { useCityEscapeFlowDialog } from "../../../providers/CityEscapeFlowDialogProvider";
import { useCityEscapeMatchOverviewDialog } from "../../../providers/CityEscapeMatchOverviewDialogProvider";
import { GlobalStatsSelection } from "../../../types/stats";
import {
  calculateAvgErrorsPerEnigma,
  calculateAvgHintsPerEnigma,
  calculateAvgTimePerMatch,
  calculateCompletionRate,
  calculateSolutionRequestRate,
  formatNumber,
  formatPercentage,
} from "../../../utils/cityEscapeSummary";

type EnrichedCityEscapeSummary = AggregatedMatchesSummary & {
  id: string;
  title: string;
  completionRate: number;
  avgHintsPerEnigma: number;
  avgErrorsPerEnigma: number;
  avgTimePerMatch: number;
  avgTimePerEnigma: number;
  avgEnigmasPerMatch: number;
  solutionRequestRate: number;
  avgRating: number | null;
};

/**
 * Arricchisce i dati grezzi con statistiche calcolate
 */
const enrichSummaryData = (
  summary: AggregatedMatchesSummary
): EnrichedCityEscapeSummary => {
  const completionRate = calculateCompletionRate(summary);
  const avgHintsPerEnigma = calculateAvgHintsPerEnigma(summary);
  const avgErrorsPerEnigma = calculateAvgErrorsPerEnigma(summary);
  const avgTimePerMatch = calculateAvgTimePerMatch(summary);
  const solutionRequestRate = calculateSolutionRequestRate(summary);
  const avgEnigmasPerMatch = summary.totalEnigmas / summary.totalMatches;
  const avgRating = summary?.ratedMatches
    ? summary.totalRating / summary.ratedMatches
    : null;

  return {
    ...summary,
    id: summary.id,
    completionRate,
    avgHintsPerEnigma,
    avgErrorsPerEnigma,
    avgTimePerMatch,
    avgTimePerEnigma: summary.avgTimePerEnigma,
    avgEnigmasPerMatch,
    avgRating,
    solutionRequestRate,
  };
};

function CityEscapeTitleCell({
  params,
  type = "cityEscape",
}: {
  params: any;
  type?: "product" | "cityEscape";
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const matchOverviewDialog = useCityEscapeMatchOverviewDialog();
  const flowDialog = useCityEscapeFlowDialog();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (type === "product") {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          aria-label="more"
          id="long-button"
          aria-controls={open ? "long-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="long-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem
            onClick={() => {
              const url = new URL(
                `/products/${params.row.id}`,
                import.meta.env.VITE_SHOPIFY_ADMIN_URL
              );
              window.open(url.toString(), "_blank");
              handleClose();
            }}
          >
            Vedi su Shopify
          </MenuItem>
        </Menu>
        <span>{params.value}</span>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            matchOverviewDialog.openModal({
              cityEscapeId: params.row.cityEscapeId,
              title: params.value,
            });
            handleClose();
          }}
        >
          Vedi Partite
        </MenuItem>
        <MenuItem
          onClick={() => {
            flowDialog.openModal({
              cityEscapeId: params.row.cityEscapeId,
              title: params.value,
            });
            handleClose();
          }}
        >
          Vedi Flow
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate({
              pathname: `/city-escape`,
              search: `?ce=${params.row.cityEscapeId}`,
            });
            handleClose();
          }}
        >
          Vai a dettaglio
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate({
              pathname: `/enigmas`,
              search: `?ce=${params.row.cityEscapeId}`,
            });
          }}
        >
          Vai a dettaglio enigmi
        </MenuItem>
        <MenuItem
          onClick={() => {
            const url = new URL(
              `admin/content/city_escapes/${params.row.cityEscapeId}`,
              import.meta.env.VITE_DIRECTUS_URL
            );
            window.open(url.toString(), "_blank");
            handleClose();
          }}
        >
          Vedi su Admin
        </MenuItem>
      </Menu>
      <span>{params.value}</span>
    </Stack>
  );
}

/**
 * Definisce le colonne del DataGrid
 */
const createColumns = (type: "product" | "cityEscape"): GridColDef[] => [
  {
    field: "title",
    headerName: "City Escape Title",
    width: 300,
    pinnable: true,
    sortable: true,
    filterable: false,
    renderCell: (params) => {
      return <CityEscapeTitleCell params={params} type={type} />;
    },
  },
  {
    field: "avgRating",
    headerName: "Avg Rating",
    type: "number",
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatNumber(value, { defaultValue: "N/D" }),
  },
  {
    field: "totalMatches",
    headerName: "Total Matches",
    type: "number",
    width: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: "completedMatches",
    headerName: "Completed",
    type: "number",
    width: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: "completionRate",
    headerName: "Completion Rate",
    type: "number",
    width: 140,
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatPercentage(value, { defaultValue: "N/D" }),
  },
  {
    field: "avgTimePerMatch",
    headerName: "Avg Time/Match",
    width: 130,
    sortable: true,
    filterable: false,
    valueFormatter: (value) =>
      formatSecondsToDDHHMMSS(value, { defaultValue: "N/D" }),
  },
  {
    field: "totalEnigmas",
    headerName: "Total Enigmas",
    type: "number",
    width: 130,
    sortable: true,
    filterable: false,
  },
  {
    field: "avgEnigmasPerMatch",
    headerName: "Avg Enigmas/Match",
    type: "number",
    width: 130,
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatNumber(value, { defaultValue: "N/D" }),
  },
  {
    field: "avgTimePerEnigma",
    headerName: "Avg Time/Enigma",
    width: 130,
    sortable: true,
    type: "number",
    filterable: false,
    valueFormatter: (value) =>
      formatSecondsToDDHHMMSS(value, { defaultValue: "N/D" }),
  },
  {
    field: "avgHintsPerEnigma",
    headerName: "Avg Hints/Enigma",
    width: 150,
    sortable: true,
    filterable: false,
    type: "number",
    valueFormatter: (value) => formatNumber(value, { defaultValue: "N/D" }),
  },
  {
    field: "avgErrorsPerEnigma",
    headerName: "Avg Errors/Enigma",
    width: 150,
    sortable: true,
    filterable: false,
    type: "number",
    valueFormatter: (value) => formatNumber(value, { defaultValue: "N/D" }),
  },
  {
    field: "solutionRequestRate",
    headerName: "Solution Request Rate",
    type: "number",
    width: 180,
    filterable: false,
    sortable: true,
    valueFormatter: (value) => formatPercentage(value, { defaultValue: "N/D" }),
  },
];

/**
 * Componente LoadingState separato per chiarezza
 */
const LoadingState: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height={400}>
    <Typography>Loading city escape summaries...</Typography>
  </Box>
);

/**
 * Componente ErrorState separato per chiarezza
 */
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <Alert severity="error" sx={{ margin: 2 }}>
    Error loading data: {error}
  </Alert>
);

/**
 * Componente principale CityEscapeTable
 */
export const CityEscapeTable = ({
  selection,
  filters,
  pending,
}: {
  selection: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) => {
  const cityEscapeSummaries = useCityEscapesSummaries(
    { selection, filters, opts: { verbose: false } },
    { enabled: !pending && selection.type === "cityEscape" }
  );

  const productSummaries = useProductSummaries(
    { selection, filters, opts: { verbose: false } },
    { enabled: !pending && selection.type === "product" }
  );

  const apiRef = useGridApiRef();
  useEffect(() => {
    if (apiRef.current) {
      console.log(apiRef.current.state.rows);
    }
  }, [apiRef.current?.state.rows]);

  const summaries = useMemo(() => {
    if (selection.type === "cityEscape") {
      return cityEscapeSummaries.data;
    } else if (selection.type === "product") {
      return productSummaries.data;
    }
    return undefined;
  }, [selection.type, cityEscapeSummaries.data, productSummaries.data]);

  const enrichedData = useMemo(
    () => summaries?.map(enrichSummaryData),
    [summaries]
  );

  const columns = useMemo(
    () => createColumns(selection.type),
    [selection.type]
  );
  const firstColumnStickyStyles = useFirstColumnStickyStyles();

  if (cityEscapeSummaries.isLoading) {
    return <LoadingState />;
  }

  if (cityEscapeSummaries.error) {
    return <ErrorState error={cityEscapeSummaries.error?.message} />;
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        City Escape Statistics
      </Typography>

      <DataGrid
        apiRef={apiRef}
        rows={enrichedData}
        disableVirtualization
        onStateChange={(state: GridEventListener<"stateChange">) => {
          state;
        }}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: {},
          },
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
          sorting: {
            sortModel: [{ field: "totalMatches", sort: "desc" }],
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        sx={firstColumnStickyStyles}
      />
    </Box>
  );
};

export default CityEscapeTable;
